import { useCallback, useEffect, useState } from 'react';

import type { AuthFlow, AuthResponse, OriginName } from '~/interfaces/common';
import type { AccountProfileData } from '~/routes/auth/signup/-types/types';
import { createLoginHandler } from '~/services/login/factory';
import { createSignupHandler } from '~/services/signup/factory';
import { fetchJson } from '~/utils/fetch-json';
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from '~/utils/local-storage';
import { redirectAfterAuth } from '~/utils/redirect-after-auth';

interface OauthProps {
  code?: string | null;
  state?: string | null;
  authFlow?: string | null;
  authorizationUrl?: string | null;
  data?: string | null;
  redirect?: string | null;
}

export function useOauth({
  code,
  state,
  authFlow,
  authorizationUrl,
  data,
  redirect,
}: OauthProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const handleAuth = useCallback(
    async (authFlow: AuthFlow, originName: OriginName) => {
      setIsLoading(true);
      try {
        let res!: AuthResponse | null;
        if (authFlow === 'login') {
          setMessage('Iniciando sesion');
          const handler = createLoginHandler(originName);
          res = await handler.handle({
            assert: {
              code,
              state,
            },
          });
        }

        let usage: string | undefined;
        if (authFlow === 'signup') {
          setMessage('Creando cuenta');

          const data = getLocalStorageItem<AccountProfileData>('signup');
          if (!data) {
            throw new Error(
              'Ocurrio un error al obtener los datos de registro por OAuth',
            );
          }

          const handle = createSignupHandler(originName);
          res = await handle.handle({
            assert: {
              code,
              state,
            },
            data,
          });
          usage = data.usage;
        }

        if (!res) {
          console.log('no res');
          return;
        }
        const token = res.result.access_token;

        if (usage) {
          await fetchJson('/identities/me', {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              metadata: {
                usage: usage,
              },
            }),
          });
        }

        redirectAfterAuth(token);
      } catch (error) {
        console.error('OAuth error:', error);
      } finally {
        setMessage(null);
        setIsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (code && state) {
      const authFlow = getLocalStorageItem<AuthFlow>('authFlow');
      const originName = getLocalStorageItem<OriginName>('originName');
      if (authFlow && originName) {
        handleAuth(authFlow, originName);
      }
    }

    if (redirect) {
      setLocalStorageItem('redirect', redirect);
    }

    if (authorizationUrl && authFlow) {
      if (data) {
        setLocalStorageItem('signup', JSON.parse(data));
      }
      setLocalStorageItem('authFlow', authFlow);
      setLocalStorageItem('originName', 'bloque-auth0');
      window.location.href = authorizationUrl;
    }
  }, []);

  return {
    isLoading,
    message,
  };
}
